import React from 'react';
import { Briefcase, CheckCircle2, Compass, LockKeyhole, Search, ShieldCheck, Users } from 'lucide-react';
import { Button } from '../components/ui/Button';
const employerPrinciples = [{
  icon: Search,
  title: 'Disciplined market mapping',
  desc: 'We begin with the leadership outcome, target universe, and success profile—not a database search.'
}, {
  icon: Users,
  title: 'Senior-led engagement',
  desc: 'A senior consultant remains accountable through scoping, assessment, offer, and integration.'
}, {
  icon: LockKeyhole,
  title: 'Confidential by design',
  desc: 'Sensitive mandates are managed through clear communication protocols and controlled stakeholder access.'
}, {
  icon: ShieldCheck,
  title: 'Long-term accountability',
  desc: 'We focus on the quality of the leadership decision, the integrity of the process, and onboarding readiness.'
}];
export function ForEmployersPage() {
  return <div className="bg-white pt-20" data-id="element-466" data-mp="12wgf7eg6yoh1">
      <section className="bg-navy-900 text-white py-24" data-id="element-467" data-mp="2t9rze3lwjvfl">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 items-end" data-id="element-468" data-mp="2tbctxt87vedg">
          <div className="lg:col-span-8" data-id="element-469" data-mp="2ya62r8ck6h7a">
            <div className="flex items-center gap-3 mb-7" data-id="element-470" data-mp="338hexpqbt3so">
              <div className="w-12 h-px bg-gold-500" data-id="element-471" data-mp="3791awpz5y1d8" />
              <span className="text-gold-500 font-semibold tracking-[0.25em] uppercase text-xs" data-id="element-472" data-mp="377q5ymtzenkj">For Boards & Leadership Teams</span>
            </div>
            <h1 className="text-4xl md:text-6xl font-serif font-bold leading-tight mb-7" data-id="element-473" data-mp="2fhva07bmbzmw">
              Secure the leader your next chapter depends on.
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl leading-relaxed" data-id="element-474" data-mp="2ji5l1ye9pg8m">
              TalentBridge partners with boards, CEOs, and investors on retained executive search, succession, and leadership advisory for consequential appointments.
            </p>
          </div>
          <div className="lg:col-span-4 lg:text-right" data-id="element-475" data-mp="1vsjozjt6zh4u">
            <Button variant="gold" size="lg" href="/contact" data-id="element-476" data-mp="2z4ie1v7eojm0">Discuss a confidential mandate</Button>
          </div>
        </div>
      </section>

      <section className="py-20 bg-white" data-id="element-477" data-mp="2u4w1zp3bd9es">
        <div className="max-w-7xl mx-auto px-6 lg:px-12" data-id="element-478" data-mp="38ulgsn4bxbxj">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-14" data-id="element-479" data-mp="38u1z97ilcpk1">
            <div className="lg:col-span-5" data-id="element-480" data-mp="33v8mjhb4p2kx">
              <h2 className="text-4xl font-serif font-bold text-navy-900 leading-tight" data-id="element-481" data-mp="33xl9k8vqu34u">A search process built for scrutiny.</h2>
            </div>
            <p className="lg:col-span-6 lg:col-start-7 text-lg text-gray-700 leading-relaxed" data-id="element-482" data-mp="2b7mgm2efhluw">
              The most important leadership decisions are not transactional hiring exercises. We bring structured research, rigorous assessment, and confidential counsel to searches where judgment matters.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-gray-200" data-id="element-483" data-mp="4esfhv4x8fol">
            {employerPrinciples.map(item => <div key={item.title} className="bg-white p-8" data-id="element-484" data-mp="7ykh2oaj355b">
                <item.icon className="h-8 w-8 text-gold-500 mb-6" data-id="element-485" data-mp="7wznp1zy9s91" />
                <h3 className="text-xl font-serif font-bold text-navy-900 mb-3" data-id="element-486" data-mp="cyf6soikhoos">{item.title}</h3>
                <p className="text-sm text-gray-700 leading-relaxed" data-id="element-487" data-mp="3u6keirfu6rmv">{item.desc}</p>
              </div>)}
          </div>
        </div>
      </section>

      <section className="py-20 bg-gray-50" data-id="element-488" data-mp="qosf4rzdf5en">
        <div className="max-w-7xl mx-auto px-6 lg:px-12" data-id="element-489" data-mp="qq3iwrstgd5r">
          <div className="flex items-center gap-3 mb-6" data-id="element-490" data-mp="vnlukpy7x6hg">
            <div className="w-10 h-px bg-gold-500" data-id="element-491" data-mp="vnbzinybg94w" />
            <span className="text-gold-600 font-semibold tracking-[0.22em] uppercase text-xs" data-id="element-492" data-mp="gzin1c4j1mqj">Mandates We Support</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-serif font-bold text-navy-900 mb-10" data-id="element-493" data-mp="lwr4tblrjor9">Executive, board, and leadership-critical appointments.</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-px bg-gray-200" data-id="element-494" data-mp="1eed2ro6gyqu9">
            {['Technology', 'Financial Services', 'Healthcare & Life Sciences', 'Industrial & Manufacturing', 'Aerospace & Defense', 'Private Equity & Growth'].map(sector => <div key={sector} className="bg-white p-6 text-sm font-semibold text-navy-900" data-id="element-495" data-mp="3hyfl08asy0e2">{sector}</div>)}
          </div>
        </div>
      </section>

      <section className="py-20 bg-white" data-id="element-496" data-mp="37zgkjd06xb25">
        <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center" data-id="element-497" data-mp="380jbfkxijdi2">
          <Briefcase className="h-8 w-8 text-gold-500 mx-auto mb-6" data-id="element-498" data-mp="9fzxpw1emdfu" />
          <h2 className="text-3xl md:text-4xl font-serif font-bold text-navy-900 mb-5" data-id="element-499" data-mp="4h6ju2i7gkf5">Begin with a confidential scoping conversation.</h2>
          <p className="text-lg text-gray-700 leading-relaxed mb-8" data-id="element-500" data-mp="3vuaszj3nxr2o">Tell us about the leadership challenge—not just the job title. A senior consultant will respond through our verified channels.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center" data-id="element-501" data-mp="s8bwy9wrgylr">
            <Button variant="gold" size="lg" href="/contact" data-id="element-502" data-mp="ner9hbssnn0t">Request a consultation</Button>
            <span className="inline-flex items-center gap-2 text-sm text-gray-600" data-id="element-503" data-mp="vqj3zdg2054m"><CheckCircle2 className="h-4 w-4 text-gold-500" data-id="element-504" data-mp="zprxch76m0zn" />All inquiries are held in strict confidence.</span>
          </div>
        </div>
      </section>
    </div>;
}